import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useLivePredictionStats, useSubmitLivePrediction } from '@/features/games/livePredict';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { FixtureWithRefs } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

type Side = 'home' | 'away';

function PickSide({
  side,
  team,
  active,
  disabled,
  onPick,
}: {
  side: Side;
  team: FixtureWithRefs['home_team'];
  active: boolean;
  disabled: boolean;
  onPick: (side: Side) => void;
}) {
  const palette = useThemePalette();
  return (
    <Pressable
      onPress={() => onPick(side)}
      disabled={disabled}
      className="flex-1 active:opacity-80"
      style={{
        borderRadius: 14,
        borderWidth: 1.5,
        paddingVertical: 12,
        alignItems: 'center',
        gap: 6,
        borderColor: active ? palette.volt : palette.line,
        backgroundColor: active ? palette.voltGlass : palette.inset,
      }}
    >
      {team?.logo_url ? (
        <Image source={{ uri: team.logo_url }} style={{ width: 34, height: 34 }} contentFit="contain" cachePolicy="memory-disk" />
      ) : (
        <View className="h-9 w-9 rounded-full bg-surface-2" />
      )}
      <AppText variant="caption" weight="bold" numberOfLines={1} className={active ? 'text-volt-bright' : 'text-ink'}>
        {team?.short_name ?? team?.name ?? '—'}
      </AppText>
    </Pressable>
  );
}

/**
 * "Who scores the NEXT goal?" - a live-only engagement card. The pick is
 * settled in real time the moment a goal lands (correct side = +15 coins),
 * so the card resets to a fresh question after every goal.
 */
export function LivePredictCard({ fixture }: { fixture: FixtureWithRefs }) {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const palette = useThemePalette();
  const toast = useToast();
  const { userId } = useAuth();
  const live = fixture.status === 'live';
  const statsQ = useLivePredictionStats(fixture.id, live);
  const submit = useSubmitLivePrediction(fixture.id);

  const stats = statsQ.data ?? { home: 0, away: 0, mine: null };
  const total = stats.home + stats.away;
  const homePct = total > 0 ? Math.round((stats.home / total) * 100) : 50;
  const awayPct = 100 - homePct;

  const onPick = (pick: Side) => {
    if (!userId) {
      toast.show({ message: t('livePredict.signIn'), tone: 'info' });
      return;
    }
    submit.mutate(pick, {
      onSuccess: () => toast.show({ message: t('livePredict.locked'), tone: 'success' }),
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });
  };

  return (
    <Card className="mx-4 mb-4 gap-3 px-4 py-4">
      <View className="flex-row items-center gap-2">
        <View className="h-7 w-7 items-center justify-center rounded-full" style={{ backgroundColor: palette.voltGlass }}>
          <Ionicons name="flash" size={15} color={palette.voltBright} />
        </View>
        <View className="flex-1">
          <AppText variant="caption" weight="bold" className="text-ink">
            {t('livePredict.title')}
          </AppText>
          <AppText variant="micro" className="text-muted">
            {t('livePredict.subtitle')}
          </AppText>
        </View>
        <View className="flex-row items-center gap-1 rounded-full px-2 py-0.5" style={{ backgroundColor: palette.captain + '22' }}>
          <Ionicons name="server" size={11} color={palette.captain} />
          <AppText variant="micro" weight="bold" tabular style={{ color: palette.captain }}>
            +{formatNumber(15, numeralOptions)}
          </AppText>
        </View>
      </View>

      <View className="flex-row items-center gap-2">
        <PickSide side="home" team={fixture.home_team} active={stats.mine === 'home'} disabled={submit.isPending} onPick={onPick} />
        <AppText variant="micro" weight="bold" className="text-muted">
          {t('livePredict.vs')}
        </AppText>
        <PickSide side="away" team={fixture.away_team} active={stats.mine === 'away'} disabled={submit.isPending} onPick={onPick} />
      </View>

      {total > 0 ? (
        <View className="gap-1">
          <View className="h-2 flex-row overflow-hidden rounded-full" style={{ backgroundColor: palette.inset }}>
            <View style={{ width: `${homePct}%`, backgroundColor: palette.volt }} />
            <View style={{ width: `${awayPct}%`, backgroundColor: palette.captain }} />
          </View>
          <View className="flex-row items-center justify-between">
            <AppText variant="micro" tabular className="text-muted">
              {formatNumber(homePct, numeralOptions)}%
            </AppText>
            <AppText variant="micro" className="text-soft">
              {t('livePredict.community', { count: formatNumber(total, numeralOptions) })}
            </AppText>
            <AppText variant="micro" tabular className="text-muted">
              {formatNumber(awayPct, numeralOptions)}%
            </AppText>
          </View>
        </View>
      ) : (
        <AppText variant="micro" className="text-center text-muted">
          {t('livePredict.beFirst')}
        </AppText>
      )}

      {stats.mine ? (
        <View className="flex-row items-center justify-center gap-1.5">
          <Ionicons name="checkmark-circle" size={13} color={palette.win} />
          <AppText variant="micro" className="text-soft">
            {t('livePredict.yourPickIn')}
          </AppText>
        </View>
      ) : null}
    </Card>
  );
}
